import { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
interface StatItemProps {
  value: number
  suffix?: string
  label: string
  description: string
  iconSrc: string
  delay: number
  isInView: boolean
  compact?: boolean
  animate?: boolean
}

function formatCompactNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  }
  return num.toString()
}

function AnimatedNumber({ value, suffix = '', isInView, compact = false, animate = false }: { value: number; suffix?: string; isInView: boolean; compact?: boolean; animate?: boolean }) {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    if (!isInView) return

    const duration = 2000
    const steps = 60
    const increment = value / steps
    let current = 0
    const stepDuration = duration / steps

    const timer = setInterval(() => {
      current += increment
      if (current >= value) {
        setDisplayValue(value)
        clearInterval(timer)
      } else {
        setDisplayValue(Math.floor(current))
      }
    }, stepDuration)

    return () => clearInterval(timer)
  }, [value, isInView])

  const formattedValue = compact
    ? formatCompactNumber(displayValue) + suffix
    : displayValue.toLocaleString()

  const content = (
    <span>
      {formattedValue}
      {!compact && suffix}
    </span>
  )

  if (animate) {
    return (
      <motion.div
        animate={{ x: [-12, 8, -12, -32, -12] }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        {content}
      </motion.div>
    )
  }

  return content
}

function StatItem({ value, suffix, label, description, iconSrc, delay, isInView, compact, animate }: StatItemProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
    >
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-500 hover:border-[#f75e47]/30 hover:shadow-lg hover:shadow-[#f75e47]/5">
        {/* Corner accent */}
        <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br from-[#f75e47]/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

        {/* 3D Icon */}
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: delay * 2,
          }}
          className="mb-6 h-16 w-16 transition-transform duration-300 group-hover:scale-110"
        >
          <img
            src={iconSrc}
            alt=""
            className="h-full w-full object-contain drop-shadow-lg"
            loading="lazy"
          />
        </motion.div>

        {/* Number - uniform sizing for all cards */}
        <div className="mb-2 whitespace-nowrap font-bold tracking-tight text-slate-900 text-4xl md:text-5xl">
          <AnimatedNumber value={value} suffix={suffix} isInView={isInView} compact={compact} animate={animate} />
        </div>

        {/* Label */}
        <p className="text-sm font-semibold uppercase tracking-wide text-slate-900">
          {label}
        </p>

        {/* Description */}
        <p className="mt-2 text-sm text-slate-500">
          {description}
        </p>

        {/* Bottom line accent */}
        <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-[#f75e47] to-[#f75e47]/50 transition-all duration-500 group-hover:w-full" />
      </div>
    </motion.div>
  )
}

export function StatsSection() {
  const { t } = useTranslation()
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const stats = [
    {
      value: 36,
      suffix: '',
      label: t('company.statYearsLabel'),
      description: t('homepage.stats.yearsDesc'),
      iconSrc: '/images/stats/cilindro.png',
      compact: false,
    },
    {
      value: 581,
      suffix: '',
      label: t('company.statClientsLabel'),
      description: t('homepage.stats.clientsDesc'),
      iconSrc: '/images/stats/quadrato.png',
      compact: false,
    },
    {
      value: 125,
      suffix: '',
      label: t('company.statOperatorsLabel'),
      description: t('homepage.stats.operatorsDesc'),
      iconSrc: '/images/stats/piramide.png',
      compact: false,
    },
    {
      value: 11925,
      suffix: '+',
      label: t('company.statProjectsLabel'),
      description: t('homepage.stats.projectsDesc'),
      iconSrc: '/images/stats/cerchio.png',
      compact: true,
      animate: true,
    },
  ]

  return (
    <section ref={ref} className="py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Header - Modern style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 flex flex-col items-center text-center"
        >
          {/* Title */}
          <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
            {t('homepage.stats.titleAlt')}{' '}
            <span className="text-[#f75e47]">{t('homepage.stats.growth')}</span>{' '}
            {t('homepage.stats.andSuccess')}
          </h2>

          {/* Subtitle */}
          <p className="mt-4 max-w-xl text-lg text-slate-500">
            {t('homepage.stats.subtitle')}
          </p>
        </motion.div>

        {/* Stats Grid - Modern bento style */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <StatItem
              key={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              label={stat.label}
              description={stat.description}
              iconSrc={stat.iconSrc}
              delay={index * 0.1}
              isInView={isInView}
              compact={stat.compact}
              animate={stat.animate}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
